import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckOutlined,
  MinusOutlined,
  ArrowLeftOutlined,
  ExclamationOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setOrgMode, clearSetupError, fetchCustomerById } from "../../../redux/customer/customerSlice";
import { updateUserOrgMode } from "../../../redux/auth/authSlice";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const OrganizationSetup = ({ userId: propUserId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId: paramsUserId } = useParams();
  const userId = propUserId || paramsUserId;
  
  const { loading: apiLoading, error: apiError } = useSelector(
    (state) => state.customer,
  );

  const user = useSelector((state) => state.auth.user);
  const orgMode = user?.orgStructureMode;
  const orgModeLocked = user?.orgModeLocked;

  console.log(orgMode, "orgModeorgMode", orgModeLocked, "orgModeLocked");

  const [selectedStructure, setSelectedStructure] = useState(null);

  // If orgMode is already locked, redirect to Department page
  React.useEffect(() => {
    if (orgModeLocked) {
      navigate("/organization/organization/Department", { replace: true });
    }
  }, [orgModeLocked, navigate]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Auto-close error popup
  React.useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => {
        dispatch(clearSetupError());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [apiError, dispatch]);

  const structures = [
    {
      id: "location-first",
      optionText: "Option 1",
      title: "Location → Department",
      mainNode: "Warehouse Mumbai",
      children: ["Sales", "Transport", "Marketing", "IT", "Admin"],
    },
    {
      id: "department-first",
      optionText: "Option 2",
      title: "Department → Location",
      mainNode: "Sales",
      children: [
        "Warehouse Mumbai",
        "Warehouse Delhi",
        "Corporate Office Delhi",
        "Corporate Office Mumbai",
        "Headquarters",
      ],
    },
  ];

  const handleContinueClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    const result = await dispatch(setOrgMode({ userId, orgStructureMode: selectedStructure }));
    if (setOrgMode.fulfilled.match(result)) {
      if (propUserId) {
        // Admin panel context
        dispatch(fetchCustomerById(userId));
        setIsModalOpen(false);
      } else {
        // Self-setup context
        dispatch(updateUserOrgMode(selectedStructure)); 
        setIsModalOpen(false);
        navigate("/organization/organization/Department", { replace: true });
      }
    } else if (setOrgMode.rejected.match(result)) {
      // Handle the case where the backend says it's already locked
      const errorMessage = result.payload || "";
      if (
        errorMessage.includes("already locked") ||
        errorMessage.includes("cannot be changed")
      ) {
        dispatch(updateUserOrgMode(selectedStructure)); // Force sync localStorage
        setIsModalOpen(false);
        dispatch(clearSetupError()); // Clear the error so popup doesn't show
        navigate("/organization/organization/Department", { replace: true });
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6 relative">
      {/* Header / Back Button */}
      <div className="w-full max-w-5xl mb-4 flex items-center">
        {/* <div
          className="w-[32px] h-[32px] rounded-full border-2 border-[#06513D] flex items-center justify-center cursor-pointer mr-3 transition-colors hover:bg-[#06513D] hover:text-white text-[#06513D] group"
          onClick={() => navigate(-1)}
        >
          <ArrowLeftOutlined className="text-[16px] font-bold group-hover:text-white" />
        </div> */}

        <h1
          className="text-[#06513D] text-[18px] sm:text-[22px] font-bold m-0"
          style={{ fontFamily: "Calibri, sans-serif" }}
        >
          Organization Setup
        </h1>
      </div>

      {/* Main Content Card */}
      <div className="w-full  bg-white rounded-[10px] border border-[#06513D] shadow-sm overflow-hidden pb-12 pt-8">
        <h2
          className="text-[#06513D] text-[22px] sm:text-[28px] font-bold mb-10 text-center tracking-wide"
          style={{ fontFamily: "Calibri, sans-serif" }}
        >
          Define Your Organization Structure
        </h2>

        <div className="flex flex-wrap justify-center gap-8 md:gap-10 px-6 sm:px-10">
          {structures.map((structure) => {
            const isSelected = selectedStructure === structure.id;
            return (
              <div
                key={structure.id}
                className={`w-full max-w-[360px] cursor-pointer rounded-[20px] bg-white transition-all duration-300 ${
                  isSelected
                    ? "border-[1px] border-[#06513D] shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ring-[#D0E3D6]"
                    : "border border-[#E5EAEF] shadow-sm hover:shadow-md"
                }`}
                onClick={() => setSelectedStructure(structure.id)}
              >
                {/* Top Section */}
                <div className="p-6 pb-5 flex items-center border-b border-[#E5EAEF]">
                  {isSelected ? (
                    <div className="w-10 h-10 rounded-full bg-[#06513D] flex items-center justify-center mr-4 flex-shrink-0">
                      <CheckOutlined className="text-white text-xl" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#D0E3D6] border-2 border-[#06513D] mr-4 flex-shrink-0" />
                  )}
                  <div>
                    <div className="text-[13px] text-[#6B7280] mb-0.5">
                      {structure.optionText}
                    </div>
                    <div className="text-[19px] text-[#1A1A1A] tracking-wide">
                      {structure.title}
                    </div>
                  </div>
                </div>

                {/* Bottom Section */}
                <div className="p-6 pt-5">
                  <div className="text-[15px] text-[#1A1A1A] mb-5 tracking-wide">
                    Example:
                  </div>

                  <div className="relative pl-1">
                    {/* Vertical Line */}
                    <div className="absolute left-[23px] top-[20px] bottom-2 w-[2px] bg-[#06513D] z-0" />

                    {/* Main Node */}
                    <div className="flex items-center mb-7 relative z-10 w-max pr-2">
                      {isSelected ? (
                        <div className="w-10 h-10 rounded-full bg-[#06513D] flex items-center justify-center flex-shrink-0">
                          <MinusOutlined className="text-white text-[18px]" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#D0E3D6] border-[2px] border-[#06513D] flex-shrink-0" />
                      )}
                      <span className="ml-[18px] text-[17px] font-bold text-[#06513D] tracking-wide">
                        {structure.mainNode}
                      </span>
                    </div>

                    {/* Children Nodes */}
                    <div className="flex flex-col gap-5 w-full">
                      {structure.children.map((child, idx) => (
                        <div
                          key={idx}
                          className="flex items-center relative z-10 pl-[50px]"
                        >
                          {/* Horizontal connector */}
                          <div className="absolute left-[20px] top-1/2 w-[30px] h-[2px] bg-[#06513D] z-0 -translate-y-1/2" />
                          {/* Child Node Icon */}
                          <div className="w-[14px] h-[14px] rounded-full bg-[#D0E3D6] border-[1.5px] border-[#06513D] flex-shrink-0 z-10" />
                          <span className="ml-[14px] text-[15px] text-[#1A1A1A]">
                            {child}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <div className="flex justify-center mt-12">
          <button
            className={`text-white transition-colors duration-200 px-12 h-[42px] rounded-md text-[15px] font-semibold tracking-wide border-none ${
              !selectedStructure
                ? "bg-[#98A2B3] cursor-not-allowed"
                : "bg-[#06513D] hover:bg-[#054131] cursor-pointer"
            }`}
            onClick={handleContinueClick}
            disabled={!selectedStructure}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Custom Modal with Portal */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40 px-4">
            <div className="bg-white rounded-[16px] border-[1.5px] border-[#42b8a3] p-8 sm:p-10 w-full max-w-[620px] shadow-2xl relative z-[10000] mx-auto">
              <div className="flex flex-col items-center text-center">
                {/* Alert Icon */}
                <div className="w-[72px] h-[72px] rounded-full bg-[#D0E3D6] border-2 border-[#06513D] flex items-center justify-center mb-6">
                  <ExclamationOutlined className="text-[#06513D] text-[34px] font-bold" />
                </div>

                {/* Title */}
                <h2
                  className="text-[#06513D] text-[20px] sm:text-[24px] font-bold mb-4 tracking-wide"
                  style={{ fontFamily: "Calibri, sans-serif" }}
                >
                  Confirm Organization Structure?
                </h2>

                {/* Description */}
                <p className="text-[#1A1A1A] text-[14px] sm:text-[15px] leading-[1.6] mb-8 font-medium max-w-[500px]">
                  Once your organization hierarchy is finalized, it can not be
                  changed later. This structure will be used to manage
                  locations, departments, user form assignment, and emission
                  tracking across your organization.
                </p>

                <p className="text-[#1A1A1A] text-[17px] sm:text-[19px] font-medium mb-8">
                  Do you want to continue?
                </p>

                {/* Error Message handled via PopupCard */}
                {apiError &&
                  createPortal(
                    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
                      <PopupCard
                        type="error"
                        title="Error"
                        message={apiError}
                      />
                    </div>,
                    document.body,
                  )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center w-full">
                  <button
                    onClick={handleCancel}
                    disabled={apiLoading}
                    className="px-8 sm:px-12 py-2 sm:py-2.5 rounded-md border-[1.5px] border-[#42b8a3] bg-[#E9F4F1] text-[#06513D] font-bold text-[15px] hover:bg-[#D0E3D6] transition-colors w-full sm:w-[150px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={apiLoading}
                    className="px-8 sm:px-12 py-2 sm:py-2.5 rounded-md border-[1.5px] border-[#06513D] bg-[#06513D] text-white font-bold text-[15px] hover:bg-[#054131] transition-colors w-full sm:w-[150px] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {apiLoading ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OrganizationSetup;
